/* ============================================================================
   <unc-bedtime>
   The bedtime calculator: log the day's drinks, see what is still in your blood
   when you go to bed.

   THE MODEL IS THE SAME ONE snippets/unc-hp-decay-svg.liquid USES, and it has to
   stay that way. That snippet computes the figure printed in the homepage "Sleep
   check" teaser that links here, so if the two ever disagree, one of them is
   lying to the customer. Liquid has no pow(), so the snippet does the decay as
   an hour-by-hour multiply against a lookup table; this does it continuously.
   They agree: 100mg at 15:00 plus 160mg at 17:00 on a 5-hour half-life gives
   102.6mg at 23:00 in both.

   Absorption is instant here. Real caffeine peaks around 45 minutes in. Adding
   a rise would draw a prettier curve and would put this out of step with the
   homepage figure, so it stays out.

   PROGRESSIVE ENHANCEMENT. The section server-renders a real chart of the
   default day through that same Liquid snippet. This element removes it on
   upgrade and takes over. With JS off you still get a correct chart, every
   number, and all of the copy.

   Requires assets/unc-caffeine-db.js for window.UNC_CAFFEINE_DB.
   ============================================================================ */
(function () {
  'use strict';

  var DB = window.UNC_CAFFEINE_DB || [];

  // One standard coffee, used for the "latest drink" answer. Matches the 95mg
  // the homepage cost calculator assumes, and the DB's own "Drip coffee".
  var STANDARD_COFFEE = 95;

  /* Milligrams are an abstraction. "84mg" lands for nobody; "about the same as
     a Red Bull" lands for everybody. Deliberately a short, recognisable list
     rather than the whole database, because the nearest match across 221 rows
     is usually something you have never heard of. */
  var YARDSTICKS = [
    { n: 'a cup of green tea', mg: 34 },
    { n: 'a Diet Coke', mg: 46 },
    { n: 'an espresso', mg: 77 },
    { n: 'a Red Bull', mg: 80 },
    { n: 'a mug of coffee', mg: 95 },
    { n: 'a Monster', mg: 160 },
    { n: 'two mugs of coffee', mg: 190 },
    { n: 'three mugs of coffee', mg: 285 },
  ];

  /* Drives both the filter chips and the grouping of results, so a category can
     never exist in one and not the other. renderCats skips any category with no
     rows, which is why an emptied one simply stops appearing. */
  /* ORDER MATTERS: this drives both the chips and the grouping of results, so
     browsing "All" opens on plain coffee and tea rather than on nine variants
     of a Nero. Chains come after the everyday categories for that reason. */
  var CATS = [
    ['Coffee', 'Coffee'],
    ['Tea', 'Tea'],
    ['Soft', 'Soft drinks'],
    ['Chocolate', 'Chocolate'],
    ['Energy', 'Energy drinks'],
    ['Chain', 'Coffee shops'],
    ['Pills', 'Pills'],
    ['Other', 'Other'],
  ];

  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Hours are floats past midnight and can run past 24 for after-midnight
     bedtimes, so 25.5 is 01:30. Formatting is the only place that wraps. */
  function clock(h) {
    var t = ((h % 24) + 24) % 24;
    var hh = Math.floor(t);
    var mm = Math.round((t - hh) * 60);
    if (mm === 60) {
      mm = 0;
      hh = (hh + 1) % 24;
    }
    return (hh < 10 ? '0' : '') + hh + ':' + (mm < 10 ? '0' : '') + mm;
  }

  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  if (customElements.get('unc-bedtime')) return;

  customElements.define(
    'unc-bedtime',
    class UncBedtime extends HTMLElement {
      connectedCallback() {
        this.q = function (sel) {
          return this.querySelector(sel);
        }.bind(this);

        this.chartHost = this.q('[data-bt-chart]');
        this.list = this.q('[data-bt-list]');
        if (!this.chartHost || !this.list) return;

        this.threshold = parseFloat(this.dataset.threshold) || 50;
        this.halfLife = parseFloat(this.dataset.halfLife) || 5;
        this.bedtime = parseFloat(this.dataset.bedtime) || 23;
        this.uid = 0;
        this.shown = {};
        this.touched = false;

        // The server-rendered fallback chart has done its job now.
        this.chartHost.innerHTML = '';
        this.chartHost.appendChild(this.buildChart());

        this.drinks = this.parseDay(this.dataset.day || '');

        this.onInput = this.onInput.bind(this);
        this.onClick = this.onClick.bind(this);
        this.addEventListener('input', this.onInput);
        this.addEventListener('click', this.onClick);

        this.classList.add('is-live');
        this.cat = '';

        /* THE CHART IS DRAWN IN ITS OWN PIXELS, so it must be redrawn whenever
           its box changes. The funnel keeps the breakdown `hidden` until the
           first drink, which means the first render measured a 0x0 box and fell
           back to 1000x300; that drawing was then squeezed into a ~320px column
           and every label came out about a third of its size, floating in dead
           space. Redraw on any real size change (phone rotation, the fold
           opening, the box appearing). */
        if (typeof ResizeObserver === 'function') {
          var self0 = this;
          this.sizeSeen = '';
          this.ro = new ResizeObserver(function () {
            var b = self0.chartHost.getBoundingClientRect();
            var key = Math.round(b.width) + 'x' + Math.round(b.height);
            if (b.width > 0 && key !== self0.sizeSeen) {
              self0.sizeSeen = key;
              self0.render(true);
            }
          });
          this.ro.observe(this.chartHost);
        }

        /* F7 (3 Oct 2026): the 220-row list and the category chips are built
           only when "More drinks" is opened. Most visitors use the six quick
           tiles and never need them, so the first screen stays light. */
        var more = this.q('[data-bt-more]');
        var self = this;
        var buildList = function () {
          if (self.listBuilt) return;
          self.listBuilt = true;
          self.renderCats();
          self.renderSearch('');
        };
        if (more && !more.open) {
          more.addEventListener('toggle', function () {
            if (more.open) buildList();
          });
        } else {
          buildList();
        }
        this.render();
      }

      disconnectedCallback() {
        if (this.ro) this.ro.disconnect();
        this.removeEventListener('input', this.onInput);
        this.removeEventListener('click', this.onClick);
      }

      /* ---------------------------------------------------------------------
         data
      --------------------------------------------------------------------- */

      /* "Drip coffee 08:00, Red Bull 15:00" -> drink objects. Milligrams are
         looked up from the database rather than written into the preset, so a
         corrected value in the database cannot leave a stale figure behind in a
         merchant setting. An unknown name is skipped, not guessed at. */
      parseDay(spec) {
        var self = this;
        var out = [];
        spec.split(',').forEach(function (part) {
          part = part.trim();
          if (!part) return;

          // "Name|mg|HH:MM" is the authored form. The milligrams travel with the
          // name so the server-rendered fallback chart and this one cannot open
          // on different numbers for the same drink.
          var m = part.match(/^(.+?)\|(\d+(?:\.\d+)?)\|(\d{1,2}):(\d{2})$/);
          if (m) {
            out.push({
              uid: ++self.uid,
              n: m[1].trim(),
              mg: Math.round(parseFloat(m[2])),
              hour: parseInt(m[3], 10) + parseInt(m[4], 10) / 60,
            });
            return;
          }

          // "Name HH:MM" also works, with the dose resolved from the database.
          m = part.match(/^(.+?)\s+(\d{1,2}):(\d{2})$/);
          if (!m) return;
          var row = self.find(m[1].trim());
          if (!row) return;
          out.push({
            uid: ++self.uid,
            n: row.n,
            mg: row.mg,
            hour: parseInt(m[2], 10) + parseInt(m[3], 10) / 60,
          });
        });
        return out;
      }

      /* Hand the day out as plain objects, and take it back the same way. Used
         to survive the email form's POST, which reloads the page and would
         otherwise drop the visitor back on the server's default day and take
         their result with it.

         DELIBERATELY NOT the authored "Name|mg|HH:MM, ..." spec string that
         parseDay reads. That form splits on commas, and one of the 221 drinks
         in the database has a comma in its name, so a day containing it would
         come back mangled. Nothing outside this element has to read these, so
         they do not need a text encoding at all. */
      dayspec() {
        return this.drinks.map(function (d) {
          return { n: d.n, mg: d.mg, hour: d.hour };
        });
      }

      /* The bedtime slider is a real input, so it has to be written as well as
         the property, or the readout and the control disagree until the next
         drag. Counts as touched: the day came from the visitor. */
      restore(day, bedtime) {
        if (!day || !day.length) return false;
        var self = this;
        this.drinks = day
          .filter(function (d) {
            return d && d.n && isFinite(d.mg) && isFinite(d.hour);
          })
          .map(function (d) {
            return {
              uid: ++self.uid,
              n: String(d.n),
              mg: Math.round(d.mg),
              hour: parseFloat(d.hour),
            };
          });
        if (!this.drinks.length) return false;

        var bed = parseFloat(bedtime);
        if (!isNaN(bed)) {
          this.bedtime = bed;
          var slider = this.q('[data-bt-bedtime]');
          if (slider) slider.value = bed;
        }
        this.touched = true;
        this.render();
        return true;
      }

      find(name) {
        var key = name.toLowerCase();
        for (var i = 0; i < DB.length; i++) {
          if (DB[i].n.toLowerCase() === key) return DB[i];
        }
        return null;
      }

      /* --------------------------------------------------------------------
         the model
      -------------------------------------------------------------------- */
      level(t) {
        var total = 0;
        var hl = this.halfLife;
        for (var i = 0; i < this.drinks.length; i++) {
          var d = this.drinks[i];
          if (t >= d.hour) total += d.mg * Math.pow(0.5, (t - d.hour) / hl);
        }
        return total;
      }

      /* What each individual drink is still contributing at bedtime, which is
         the same per-dose term level() sums. Drinks taken after bedtime
         contribute nothing and are dropped rather than returned as zero.

         Sorted heaviest first, so the caller wanting the one drink that owns
         the bedtime figure just takes [0]. Nothing in the calculator itself
         uses this; it exists for snippets/unc-bt-outcome.liquid. */
      contributions() {
        var hl = this.halfLife;
        var bed = this.bedtime;
        return this.drinks
          .filter(function (d) {
            return d.hour <= bed;
          })
          .map(function (d) {
            return {
              n: d.n,
              mg: d.mg,
              hour: d.hour,
              at: d.mg * Math.pow(0.5, (bed - d.hour) / hl),
            };
          })
          .sort(function (a, b) {
            return b.at - a.at;
          });
      }

      /* The latest you could have one more standard coffee and still be at or
         under the threshold at bedtime. Closed form, from
             level(bed) + 95 * 0.5^((bed - h)/hl) <= threshold
         which rearranges to  h <= bed - hl * log2(95 / headroom).
         Returns null when there is no headroom left. */
      /* The latest ONE MORE standard coffee could land and still leave the
         visitor under the threshold at bedtime.

         THIS ANSWERS A HYPOTHETICAL, NOT WHAT THEY DRANK, and the copy has to
         say so. It briefly suppressed any answer falling before their own last
         drink, on the grounds that telling someone not to drink after a time
         they had already drunk past was nonsense. That threw away a true and
         useful answer: log 2mg of decaf at 17:45 and the honest reply is that
         a full coffee would have had to land by 17:15, not that the day is
         finished. The tense is handled in the wording instead, with "would need
         to land by", which reads correctly whether the time is past or future.

         null means genuinely no room: already at or over the threshold. */
      latestDrink() {
        var headroom = this.threshold - this.level(this.bedtime);
        if (headroom <= 0) return null;
        if (headroom >= STANDARD_COFFEE) return this.bedtime;
        return this.bedtime - this.halfLife * (Math.log(STANDARD_COFFEE / headroom) / Math.LN2);
      }

      /* --------------------------------------------------------------------
         chart
      -------------------------------------------------------------------- */
      buildChart() {
        var ns = 'http://www.w3.org/2000/svg';
        var svg = document.createElementNS(ns, 'svg');
        /* The viewBox is set in render() from the element's real pixel size, so
           one user unit is one CSS pixel and nothing is distorted. It used to be
           a fixed 1000x300 with preserveAspectRatio="none", which squashed the
           drawing into whatever box it landed in: harmless for the strokes,
           which are non-scaling, but it stretched the hour labels horizontally
           by about 2x on a phone and looked broken. */
        svg.setAttribute('viewBox', '0 0 1000 300');
        svg.setAttribute('class', 'unc-bt__svg');
        svg.setAttribute('role', 'img');
        svg.setAttribute('aria-label', this.dataset.chartAlt || 'Caffeine in your blood across the day');

        // A clip that keeps only what is ABOVE the threshold line, so the same
        // area path can be painted twice and the overshoot reads at a glance.
        svg.innerHTML =
          '<defs>' +
          '<clipPath id="btOver-' + (this.dataset.uid || 'x') + '"><rect data-bt-cliprect x="0" y="0" width="1000" height="0"/></clipPath>' +
          '<linearGradient id="btFill-' + (this.dataset.uid || 'x') + '" x1="0" y1="0" x2="0" y2="1">' +
          '<stop offset="0%" stop-color="#4A90E2" stop-opacity="0.28"/>' +
          '<stop offset="100%" stop-color="#4A90E2" stop-opacity="0.02"/>' +
          '</linearGradient>' +
          '</defs>' +
          '<g data-bt-grid></g>' +
          /* THE TWO FILLS SHARE A GROUP SO THE DRAW-IN CAN WIPE THEM.
             data-bt-over already carries an SVG clip-path attribute, the one
             that keeps only what is above the threshold. A CSS clip-path on
             the same element REPLACES that attribute rather than combining
             with it, so animating the element directly stripped its threshold
             clip and painted red across the whole area until the animation
             class came off again. The group carries no clip of its own, so
             wiping it composes with the child's instead of fighting it. */
          '<g data-bt-fills>' +
          '<path data-bt-area fill="url(#btFill-' + (this.dataset.uid || 'x') + ')" d=""/>' +
          '<path data-bt-over class="unc-bt__over" clip-path="url(#btOver-' + (this.dataset.uid || 'x') + ')" d=""/>' +
          '</g>' +
          '<line data-bt-thresh class="unc-bt__thresh" x1="0" x2="1000"/>' +
          '<line data-bt-bedline class="unc-bt__bedline" y1="0" y2="300"/>' +
          '<path data-bt-line class="unc-bt__line" fill="none" d=""/>' +
          '<circle data-bt-halo class="unc-bt__halo" r="7"/>' +
          '<circle data-bt-dot class="unc-bt__dot" r="7"/>' +
          '<g data-bt-labels></g>';
        return svg;
      }

      /* The chart drawing itself in, once, when the first drink lands.

         The line is animated with stroke-dashoffset, so it needs its own length
         as a number. A hardcoded dasharray would either cut a long path short
         or leave a long pause on a short one, and the path length here changes
         with viewport width, so it has to be measured rather than guessed.

         prefers-reduced-motion is honoured by leaving the class off entirely,
         not by shortening the duration. */
      enterChart(svg) {
        /* SAME OPT-IN SHAPE AS uncAutoCycle. Reduced motion wins unless the
           section says otherwise, because that is the rule everywhere else on
           the site. It is opted in by default here: this is not ambient
           decoration, it is the page drawing the answer the visitor just asked
           for, and with it off the chart simply blinks into place with no cue
           that anything happened. */
        if (reduced && this.dataset.drawReduced !== 'run') return;

        var line = svg.querySelector('[data-bt-line]');
        if (line && typeof line.getTotalLength === 'function') {
          var len = 0;
          try {
            len = line.getTotalLength();
          } catch (e) {}
          if (len > 0) line.style.setProperty('--unc-bt-draw', Math.ceil(len));
        }

        // Removing, forcing a reflow, then re-adding is what lets this replay
        // if the visitor clears the day and starts again.
        svg.classList.remove('is-entering');
        void svg.getBoundingClientRect();
        svg.classList.add('is-entering');

        /* AND TAKE IT OFF AGAIN WHEN IT IS DONE. `is-entering` carries
           `stroke-dasharray: var(--unc-bt-draw)`, so leaving it on pins the line
           to the length measured at draw time. Any later day whose path is
           longer than that renders the excess as the gap of a dash pattern: the
           blue line simply stopped partway across and never came back.

           The longest animation in the set is the 1.5s glow, so clean up just
           after it. The timer is cleared first so rapid adds restart rather than
           tearing down a run that is still going. */
        if (this.drawTimer) clearTimeout(this.drawTimer);
        this.drawTimer = setTimeout(function () {
          svg.classList.remove('is-entering');
          if (line) line.style.removeProperty('--unc-bt-draw');
        }, 1600);
      }

      render(skipList) {
        var svg = this.chartHost.firstChild;
        if (!svg) return;

        /* THE EMPTY DAY IS A REAL STATE NOW. The page used to server-render a
           drip coffee so the chart was never blank, which meant every visitor
           arrived at a result they had not asked for. With that block gone the
           chart opens flat, and a flat line with no explanation reads as broken
           rather than as waiting. */
        var empty = !this.drinks.length;
        var hint = this.q('[data-bt-chart-empty]');
        if (hint) hint.hidden = !empty;
        if (empty) this.setAttribute('data-empty', '');
        else this.removeAttribute('data-empty');

        /* Decided here, fired further down. The draw-on animation measures the
           line with getTotalLength(), which is zero until the new `d` has
           actually been written, so it cannot run until after that.

           FIRED ON EVERY ADD, not just the first. Adding a drink changes the
           shape of the whole day, so redrawing it is the same event each time.
           Keyed on the count going UP rather than on any render, because
           dragging a time slider re-renders continuously and restarting the
           draw on every frame of a drag would be unreadable. */
        var count = this.drinks.length;
        if (this.lastCount === undefined) this.lastCount = count;
        var entering = count > this.lastCount || (this.wasEmpty && !empty);
        this.lastCount = count;
        if (this.wasEmpty === undefined) this.wasEmpty = empty;
        this.wasEmpty = empty;

        var start = 5;
        var end = Math.max(this.bedtime + 2, 25);

        // Match the coordinate system to the rendered box, 1 unit = 1 pixel.
        var box = this.chartHost.getBoundingClientRect();
        var W = Math.max(280, Math.round(box.width) || 1000);
        var H = Math.max(170, Math.round(box.height) || 300);
        svg.setAttribute('viewBox', '0 0 ' + W + ' ' + H);
        /* AXES (Tim, 15 Oct: "doesn't label x or y axis"). Room on the left for
           the mg figures, on top for the y title and the bedtime label, and
           under the plot for the hour labels and the x title. */
        var padL = 40;
        var padR = 10;
        var top = 32;
        var base = H - 50;
        var plotW = W - padL - padR;

        var self = this;
        var x = function (h) {
          return padL + ((h - start) / (end - start)) * plotW;
        };

        // Sample fine enough that the jump at each dose reads as a step.
        var pts = [];
        var peak = 0;
        for (var h = start; h <= end + 0.0001; h += 0.1) {
          var v = this.level(h);
          if (v > peak) peak = v;
          pts.push([h, v]);
        }

        /* THE PEAK IS ALWAYS AT A DOSE INSTANT, and a 0.1h sampling grid steps
           straight past it: with a drink at 16:00 the nearest samples are 15.95
           and 16.05, so the reported maximum came out about 1.5% low. Evaluate
           each dose time exactly and take the larger. */
        for (var i = 0; i < this.drinks.length; i++) {
          var dv = this.level(this.drinks[i].hour);
          if (dv > peak) peak = dv;
        }

        // Scale from the peak so editing the day rescales rather than clipping,
        // with a floor so a nearly empty day does not magnify noise.
        var maxY = Math.max(peak * 1.15, this.threshold * 2.2, 60);
        var y = function (v) {
          return base - (v / maxY) * (base - top);
        };

        var line = '';
        var i;
        for (i = 0; i < pts.length; i++) {
          line += (i ? 'L' : 'M') + x(pts[i][0]).toFixed(1) + ',' + y(pts[i][1]).toFixed(1);
        }
        var area = line + 'L' + (padL + plotW) + ',' + base + 'L' + padL + ',' + base + 'Z';

        svg.querySelector('[data-bt-line]').setAttribute('d', line);
        svg.querySelector('[data-bt-area]').setAttribute('d', area);
        svg.querySelector('[data-bt-over]').setAttribute('d', area);

        svg.querySelector('[data-bt-cliprect]').setAttribute('width', W);
        svg.querySelector('[data-bt-cliprect]').setAttribute('height', Math.max(0, y(this.threshold)).toFixed(1));

        var th = svg.querySelector('[data-bt-thresh]');
        th.setAttribute('x1', padL);
        th.setAttribute('x2', padL + plotW);
        th.setAttribute('y1', y(this.threshold).toFixed(1));
        th.setAttribute('y2', y(this.threshold).toFixed(1));

        var bx = x(this.bedtime).toFixed(1);
        var bed = svg.querySelector('[data-bt-bedline]');
        bed.setAttribute('x1', bx);
        bed.setAttribute('x2', bx);
        bed.setAttribute('y1', top - 14);
        bed.setAttribute('y2', base);

        var atBed = this.level(this.bedtime);
        var dy = y(atBed).toFixed(1);
        var dot = svg.querySelector('[data-bt-dot]');
        dot.setAttribute('cx', bx);
        dot.setAttribute('cy', dy);
        // Rides under the dot and only paints when over the threshold.
        var halo = svg.querySelector('[data-bt-halo]');
        if (halo) {
          halo.setAttribute('cx', bx);
          halo.setAttribute('cy', dy);
        }

        // Hour ticks, labelled in the visitor's own clock: every 6h on a phone, 3h wider.
        var grid = svg.querySelector('[data-bt-grid]');
        var g = '';
        var tickStep = W < 460 ? 6 : 3;
        for (var hh = Math.ceil(start / tickStep) * tickStep; hh <= end; hh += tickStep) {
          var gx = x(hh).toFixed(1);
          g +=
            '<line class="unc-bt__grid" x1="' + gx + '" x2="' + gx + '" y1="' + top + '" y2="' + base + '"/>' +
            '<text class="unc-bt__tick" x="' + gx + '" y="' + (base + 18) + '">' + clock(hh) + '</text>';
        }

        // Milligram ticks on the left, at round numbers, skipping any that would
        // sit on top of the threshold figure (which is drawn in red instead).
        var steps = [10, 20, 25, 50, 100, 200, 500];
        var yStep = steps[steps.length - 1];
        for (var si = 0; si < steps.length; si++) {
          if (maxY / steps[si] <= 4) {
            yStep = steps[si];
            break;
          }
        }
        var ty = y(this.threshold);
        for (var mv = 0; mv <= maxY; mv += yStep) {
          var my = y(mv);
          g +=
            '<line class="unc-bt__grid unc-bt__grid--h" x1="' + padL + '" x2="' + (padL + plotW) + '" y1="' + my.toFixed(1) + '" y2="' + my.toFixed(1) + '"/>';
          if (Math.abs(my - ty) > 12) {
            g += '<text class="unc-bt__ytick" x="' + (padL - 7) + '" y="' + (my + 4).toFixed(1) + '">' + mv + '</text>';
          }
        }

        // Axis titles.
        g +=
          '<text class="unc-bt__axis-t" x="0" y="11">' + esc(this.dataset.yTitle || 'Caffeine in your blood (mg)') + '</text>' +
          '<text class="unc-bt__axis-t unc-bt__axis-t--x" x="' + (padL + plotW / 2).toFixed(1) + '" y="' + (H - 6) + '">' + esc(this.dataset.xTitle || 'Time of day') + '</text>';
        grid.innerHTML = g;

        // Direct labels on the lines they belong to, so the legend is a backup.
        var lab = svg.querySelector('[data-bt-labels]');
        if (lab) {
          var bedRight = bx > W * 0.5;
          lab.innerHTML =
            '<text class="unc-bt__ytick unc-bt__ytick--thresh" x="' + (padL - 7) + '" y="' + (ty + 4).toFixed(1) + '">' + Math.round(this.threshold) + '</text>' +
            '<text class="unc-bt__lab unc-bt__lab--thresh" x="' + (bedRight ? bx - 6 : padL + 6) + '" y="' + (ty - 6).toFixed(1) + '" text-anchor="' + (bedRight ? 'end' : 'start') + '">' + esc(this.dataset.threshLabel || 'Sleep threshold') + '</text>' +
            '<text class="unc-bt__lab unc-bt__lab--bed" x="' + (bedRight ? bx - 6 : +bx + 6) + '" y="' + (top - 8) + '" text-anchor="' + (bedRight ? 'end' : 'start') + '">' +
            esc(this.dataset.bedLabel || 'Bedtime') + ' ' + clock(this.bedtime) + '</text>';
        }

        /* THE DRAW STARTS ONLY ONCE THE CHART IS FINISHED BEING BUILT.

           This used to fire immediately after the line's `d` was set, which was
           right about the line and wrong about everything else: the clip rect,
           the threshold, the bedtime line, the dot and the whole grid were all
           still to come. `grid.innerHTML` above replaces fourteen nodes, so the
           furniture was being swapped out 22ms into its own 0.45s fade, and the
           wipe was running over geometry that changed underneath it. The result
           was a chart that drew part way, sat there, and then snapped to the
           finished state.

           Everything the animation touches now exists in its final form before
           the class goes on. */
        if (entering) this.enterChart(svg);

        if (!skipList) this.renderList();
        this.markQuickChips();
        this.renderOut(atBed, peak);
      }

      renderOut(atBed, peak) {
        var t = this.threshold;
        this.tween('[data-bt-mg]', atBed);
        this.tween('[data-bt-peak]', peak);
        this.tween(
          '[data-bt-total]',
          this.drinks.reduce(function (s, d) {
            return s + d.mg;
          }, 0)
        );

        var latest = this.latestDrink();
        var el = this.q('[data-bt-latest]');
        if (el && latest !== null) el.textContent = clock(latest);

        // No headroom left is a different statement, not a different value, so
        // the whole sentence swaps rather than one word inside it.
        var cutNormal = this.q('[data-bt-cutoff]');
        var cutOver = this.q('[data-bt-cutoff-over]');
        if (cutNormal && cutOver) {
          cutNormal.hidden = latest === null;
          cutOver.hidden = latest !== null;
        }

        /* ALL of them, not the first. The bedtime now reads out twice: once on
           the slider and once inside the cutoff instruction under the chart.
           querySelector would have kept the sentence frozen on the default. */
        var bedText = clock(this.bedtime);
        Array.prototype.forEach.call(
          this.querySelectorAll('[data-bt-bedtime-out]'),
          function (el) {
            el.textContent = bedText;
          }
        );

        // Four states, described rather than prescribed. This reports what is in
        // the blood; it does not promise anything about sleep.
        var tier, label;
        if (atBed <= t * 0.4) {
          tier = 'clear';
          label = this.dataset.v1 || 'Clear by bedtime';
        } else if (atBed <= t) {
          tier = 'near';
          label = this.dataset.v2 || 'Nearly clear';
        } else if (atBed <= t * 2.5) {
          tier = 'high';
          label = this.dataset.v3 || 'Still active';
        } else {
          tier = 'peak';
          label = this.dataset.v4 || 'Wide awake';
        }
        var yard = '';
        if (atBed >= 12) {
          var best = YARDSTICKS[0];
          for (var i = 1; i < YARDSTICKS.length; i++) {
            if (Math.abs(YARDSTICKS[i].mg - atBed) < Math.abs(best.mg - atBed)) {
              best = YARDSTICKS[i];
            }
          }
          yard = best.n;
        }
        var eq = this.q('[data-bt-equiv]');
        if (eq) eq.textContent = yard ? 'Roughly ' + yard + ', drunk as you get into bed.' : '';

        var v = this.q('[data-bt-verdict]');
        if (v) {
          v.textContent = label;
          v.setAttribute('data-tier', tier);
        }
        this.setAttribute('data-tier', tier);

        /* OVER THE LINE IS THE WHOLE POINT OF THE TOOL, so it gets its own flag
           rather than being inferred from `high` or `peak` in a dozen selectors.
           72mg used to report as a gold badge reading "Still active", and gold
           is the brand accent: it looked like a pass. Everything downstream of
           this attribute (the number, the badge, the bedtime dot, the warning
           bar) turns red together off this one hook. */
        var over = atBed > t;
        if (over) this.setAttribute('data-over', '');
        else this.removeAttribute('data-over');

        var alert = this.q('[data-bt-alert]');
        if (alert) {
          alert.hidden = !over;
          var by = this.q('[data-bt-alert-by]');
          if (over && by) by.textContent = Math.round(atBed - t);
        }

        /* THE ONLY SEAM between the calculator and the funnel that follows it.
           assets/unc-bt-outcome.js listens for this and everything downstream of
           the result hangs off it. Deleting these lines turns the funnel off
           completely and leaves the calculator working exactly as before.

           `touched` is the difference between the day the section shipped with
           and a day the visitor built. The outcome section reads the visitor's
           drinks back to them by name, so it must not open on the server's
           default and claim they logged it. */
        this.dispatchEvent(
          new CustomEvent('unc:calc', {
            bubbles: true,
            detail: {
              total: Math.round(
                this.drinks.reduce(function (s, d) {
                  return s + d.mg;
                }, 0)
              ),
              residual: Math.round(atBed),
              peak: Math.round(peak),
              bedtime: this.bedtime,
              threshold: t,
              halfLife: this.halfLife,
              latest: this.latestDrink(),
              drinks: this.drinks.slice(),
              contributions: this.contributions(),
              touched: !!this.touched,
              equiv: yard,
              clock: clock,
            },
          })
        );
      }

      /* Counting between values rather than snapping, so a change reads as a
         change. Reduced motion writes the final value and returns. */
      tween(sel, to) {
        var el = this.q(sel);
        if (!el) return;
        var from = parseFloat(el.textContent.replace(/[^\d.]/g, '')) || 0;
        to = Math.round(to);
        if (reduced || from === to) {
          el.textContent = to;
          return;
        }
        if (el._raf) cancelAnimationFrame(el._raf);
        var t0 = 0;
        var step = function (ts) {
          if (!t0) t0 = ts;
          var k = Math.min(1, (ts - t0) / 420);
          var e = 1 - Math.pow(1 - k, 3);
          el.textContent = Math.round(from + (to - from) * e);
          if (k < 1) el._raf = requestAnimationFrame(step);
        };
        el._raf = requestAnimationFrame(step);
      }

      /* --------------------------------------------------------------------
         the day list
      -------------------------------------------------------------------- */
      renderList() {
        var self = this;
        var sorted = this.drinks.slice().sort(function (a, b) {
          return a.hour - b.hour;
        });
        var empty = this.q('[data-bt-empty]');
        if (empty) empty.hidden = sorted.length > 0;
        var logged = this.q('[data-bt-logged]');
        if (logged) logged.hidden = sorted.length === 0;

        /* v2 (4 Oct 2026, @marketing W3): one-line chips, "Coffee 08:00 x".
           The time is a <select> styled as text, so a tap opens the phone's own
           picker. Opted into by data-bt-list="chips"; the old card + slider
           rows stay below for any page that still uses them. */
        if (this.list.dataset.btList === 'chips') {
          var short = {};
          Array.prototype.forEach.call(this.querySelectorAll('.unc-bt__qt[data-bt-add]'), function (b) {
            var n = b.querySelector('.unc-bt__qt-n');
            if (n) short[b.dataset.btAdd.toLowerCase()] = n.textContent.trim();
          });
          this.list.innerHTML = sorted
            .map(function (d) {
              var label = short[d.n.toLowerCase()] || d.n;
              var cur = Math.round(d.hour * 4) / 4;
              var opts = '';
              for (var q = 0; q < 96; q++) {
                var h = q / 4;
                opts += '<option value="' + h + '"' + (h === cur ? ' selected' : '') + '>' + clock(h) + '</option>';
              }
              return (
                '<li class="unc-bt__lc" data-bt-drink="' + d.uid + '">' +
                '<span class="unc-bt__lc-n">' + esc(label) + '</span>' +
                '<select class="unc-bt__lc-t" data-bt-time="' + d.uid + '"' +
                ' aria-label="Time for ' + esc(d.n) + '">' + opts + '</select>' +
                '<button type="button" class="unc-bt__lc-x" data-bt-remove="' + d.uid + '"' +
                ' aria-label="Remove ' + esc(d.n) + '">' +
                '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M4 4 L12 12 M12 4 L4 12"/></svg>' +
                '</button></li>'
              );
            })
            .join('');
          return;
        }

        this.list.innerHTML = sorted
          .map(function (d) {
            return (
              '<li class="unc-bt__drink" data-bt-drink="' + d.uid + '">' +
              '<span class="unc-bt__drink-top">' +
              '<span class="unc-bt__drink-name">' + esc(d.n) + '</span>' +
              '<span class="unc-bt__drink-mg">' + d.mg + 'mg</span>' +
              '<button type="button" class="unc-bt__drink-x" data-bt-remove="' + d.uid + '"' +
              ' aria-label="Remove ' + esc(d.n) + '">' +
              '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M4 4 L12 12 M12 4 L4 12"/></svg>' +
              '</button></span>' +
              '<span class="unc-bt__drink-time">' +
              '<input type="range" class="unc-bt__range" min="0" max="24" step="0.25"' +
              ' value="' + d.hour + '" data-bt-time="' + d.uid + '"' +
              ' aria-label="Time for ' + esc(d.n) + '">' +
              '<output class="unc-bt__drink-clock">' + clock(d.hour) + '</output>' +
              '</span></li>'
            );
          })
          .join('');
      }

      /* --------------------------------------------------------------------
         search
      -------------------------------------------------------------------- */
      /* Light up a quick-add chip while that drink is in the day, so the shortcut
         row doubles as a read-out of what you have logged. Matched on name, so
         two coffees light the one chip: it answers "is this in my day", not
         "how many". */
      markQuickChips() {
        var names = {};
        this.drinks.forEach(function (d) {
          names[d.n.toLowerCase()] = true;
        });
        Array.prototype.forEach.call(this.querySelectorAll('[data-bt-add]'), function (b) {
          // search results also carry data-bt-add and must not be marked
          if (b.classList.contains('unc-bt__hit')) return;
          b.setAttribute('aria-pressed', names[b.dataset.btAdd.toLowerCase()] ? 'true' : 'false');
        });
      }

      /* Category chips, built from the same CATS list the results are grouped by,
         so a category can never appear in one and not the other. */
      renderCats() {
        var box = this.q('[data-bt-cats]');
        if (!box) return;
        var self = this;
        var html = '<button type="button" class="unc-bt__chip unc-bt__chip--cat" data-bt-cat="" aria-pressed="' + (this.cat ? 'false' : 'true') + '">All</button>';
        CATS.forEach(function (pair) {
          if (!DB.some(function (r) { return r.c === pair[0]; })) return;
          html += '<button type="button" class="unc-bt__chip unc-bt__chip--cat" data-bt-cat="' + pair[0] + '"' +
                  ' aria-pressed="' + (self.cat === pair[0] ? 'true' : 'false') + '">' + esc(pair[1]) + '</button>';
        });
        box.innerHTML = html;
      }

      renderSearch(term) {
        var box = this.q('[data-bt-results]');
        if (!box) return;
        term = (term || '').trim().toLowerCase();
        var cat = this.cat;

        var hits = DB;
        if (cat) {
          hits = hits.filter(function (r) {
            return r.c === cat;
          });
        }
        if (term) {
          hits = hits.filter(function (r) {
            return r.n.toLowerCase().indexOf(term) !== -1;
          });
        }

        if (!hits.length) {
          box.innerHTML = '<p class="unc-bt__none">' + esc(this.dataset.noResults || 'Nothing matches that. Try a shorter word.') + '</p>';
          return;
        }

        /* No cap. It used to show 12 with a "more" line, which meant the All
           chip visibly did not show all and the list looked truncated rather
           than scrollable. 221 buttons is a trivial amount of DOM and the box
           scrolls, so show the lot and let people scroll or type. */
        var shown = hits;
        var byCat = {};
        shown.forEach(function (r) {
          (byCat[r.c] = byCat[r.c] || []).push(r);
        });

        var html = '';
        CATS.forEach(function (pair) {
          var rows = byCat[pair[0]];
          if (!rows) return;
          html += '<li class="unc-bt__cat"><span class="unc-bt__cat-name">' + esc(pair[1]) + '</span><ul class="unc-bt__cat-list">';
          rows.forEach(function (r) {
            html +=
              '<li><button type="button" class="unc-bt__hit" data-bt-add="' + esc(r.n) + '">' +
              '<span class="unc-bt__hit-name">' + esc(r.n) + '</span>' +
              '<span class="unc-bt__hit-serve">' + esc(r.s) + '</span>' +
              '<span class="unc-bt__hit-mg">' + r.mg + 'mg</span>' +
              '</button></li>';
          });
          html += '</ul></li>';
        });

        box.innerHTML = html;
      }

      /* --------------------------------------------------------------------
         events
      -------------------------------------------------------------------- */
      onInput(e) {
        var t = e.target;

        if (t.matches('[data-bt-search]')) {
          this.renderSearch(t.value);
          return;
        }
        /* Typing in the search box is browsing, not logging, so it is handled
           above and does not set this. Everything past here changes the day. */
        this.touched = true;
        if (t.matches('[data-bt-time]')) {
          var uid = parseInt(t.dataset.btTime, 10);
          var d = this.drinks.filter(function (x) {
            return x.uid === uid;
          })[0];
          if (!d) return;
          d.hour = parseFloat(t.value);
          if (t.tagName === 'SELECT') {
            this.render();
            return;
          }
          // Update the label and chart without rebuilding the list, or the
          // slider would be replaced mid-drag and the pointer would drop it.
          var out = t.parentNode.querySelector('.unc-bt__drink-clock');
          if (out) out.textContent = clock(d.hour);
          this.renderChartOnly();
          return;
        }
        if (t.matches('[data-bt-bedtime]')) {
          this.bedtime = parseFloat(t.value);
          this.renderChartOnly();
          return;
        }
        if (t.matches('[data-bt-half]')) {
          this.halfLife = parseFloat(t.value);
          this.renderChartOnly();
        }
      }

      /* Everything render() does except rebuilding the list.

         This used to save the list's innerHTML, re-render, then write the HTML
         back. Writing innerHTML DESTROYS AND RECREATES EVERY NODE, including the
         slider under the user's finger, on every single input event. The pointer
         was left holding a node that no longer existed, so drink sliders juddered
         and dropped while the bedtime slider, which lives outside the list, was
         perfectly smooth. Skip the list instead of rebuilding it. */
      renderChartOnly() {
        this.render(true);
      }

      onClick(e) {
        var add = e.target.closest('[data-bt-add]');
        if (add && this.contains(add)) {
          this.touched = true;
          this.add(add.dataset.btAdd);
          return;
        }
        var rm = e.target.closest('[data-bt-remove]');
        if (rm && this.contains(rm)) {
          this.touched = true;
          var uid = parseInt(rm.dataset.btRemove, 10);
          this.drinks = this.drinks.filter(function (d) {
            return d.uid !== uid;
          });
          this.render();
          return;
        }
        var pre = e.target.closest('[data-bt-preset]');
        if (pre && this.contains(pre)) {
          this.touched = true;
          this.drinks = this.parseDay(pre.dataset.btPreset);
          var group = pre.parentNode;
          Array.prototype.forEach.call(group.querySelectorAll('[data-bt-preset]'), function (b) {
            b.setAttribute('aria-pressed', b === pre ? 'true' : 'false');
          });
          this.render();
          return;
        }
        var cat = e.target.closest('[data-bt-cat]');
        if (cat && this.contains(cat)) {
          this.cat = cat.dataset.btCat || '';
          this.renderCats();
          var search = this.q('[data-bt-search]');
          this.renderSearch(search ? search.value : '');
          return;
        }
        var clr = e.target.closest('[data-bt-clear]');
        if (clr && this.contains(clr)) {
          this.touched = true;
          this.drinks = [];
          this.render();
        }
      }

      /* A new drink lands at a sensible hour rather than at midnight: an hour
         after the last one, so tapping the same coffee three times builds a
         morning instead of stacking three doses on one instant. */
      add(name) {
        var row = this.find(name);
        if (!row) return;
        var last = this.drinks.reduce(function (m, d) {
          return Math.max(m, d.hour);
        }, 6.5);
        var hour = Math.min(23.75, this.drinks.length ? last + 1 : 8);
        this.drinks.push({ uid: ++this.uid, n: row.n, mg: row.mg, hour: hour });
        this.render();

        var el = this.list.querySelector('[data-bt-drink="' + this.uid + '"]');
        if (el) el.classList.add('is-new');
      }
    }
  );
})();
